import type { FloorPlan, Hotspot, Preset } from '../api/types.js';
import type { FloorplanPosition, FloorplanSnapshot } from '../floorplan/types.js';
import type { HotspotScreenPosition, HotspotsSnapshot } from '../hotspots/types.js';
import type { DeviceCapabilities } from '../lib/device.js';
import type { MeasurementProjection, MeasurementsSnapshot } from '../measure/types.js';
import type { AppStore } from '../state/store.js';
import type { AppState } from '../state/types.js';
import type { TourSnapshot } from '../tour/types.js';
import type { ViewboxSnapshot } from '../viewbox/types.js';
import type { PostFxSnapshot } from '../viewer/post-fx.js';
import type { WalkSnapshot } from '../walk/types.js';
import { isMobileFpEnabled } from '../walk/mobile-fp-flag.js';
import type { XRSnapshot } from '../xr/types.js';
import type { ComponentInstance } from './component.js';
import { mountCancelledByUser } from './components/cancelled-by-user.js';
import { mountErrorExpired } from './components/error-expired.js';
import { mountErrorNotFound } from './components/error-not-found.js';
import { mountErrorTransient } from './components/error-transient.js';
import { mountIdle } from './components/idle.js';
import { type LoadedCallbacks, mountLoaded } from './components/loaded.js';
import { mountLoading } from './components/loading.js';
import { showShareToast } from './components/share-toast.js';
import { mountUploadFallback } from './components/upload-fallback.js';
import { showWalkErrorToast, type WalkErrorReason } from './components/walk-error-toast.js';

export type { LoadedCallbacks };

// 5.C: layout discriminator. 'fullscreen' (default) keeps existing
// behavior — global CSS for #viewer-canvas-layer / #viewer-ui-root
// uses 100dvh + position:fixed so toolbar/hotspot/preset overlays
// pin to the viewport. 'embedded' is for the Studio author runtime:
// no global CSS injection (the wrapper already constrains size via
// position:absolute; inset:0), and components read the layout off
// the uiRoot's `data-viewer-layout` attribute to swap fixed→absolute.
export type ViewerLayout = 'fullscreen' | 'embedded';

export interface RendererDeps {
  uiRoot: HTMLElement;
  store: AppStore;
  onRetry: () => void;
  embed: boolean;
  // 5.C: default 'fullscreen' (no behavior change for src/main.ts).
  layout?: ViewerLayout;
  loadedCallbacks: LoadedCallbacks;
  orchestratorEvents: EventTarget;
  // 4.F: providers seed initial Loaded mount with the orchestrator's current
  // sorted presets + tour snapshot (no need to wait for the first event).
  getSortedPresets: () => readonly Preset[];
  getTourSnapshot: () => TourSnapshot;
  // 4.G: same provider pattern for hotspots.
  getSortedHotspots: () => readonly Hotspot[];
  getHotspotsSnapshot: () => HotspotsSnapshot;
  // 4.H: floor plan presence + snapshot. floorplan may be null when the
  // model has none attached.
  getFloorplan: () => FloorPlan | null;
  getFloorplanSnapshot: () => FloorplanSnapshot;
  // 4.J: measurement state + raycast capability. raycastAvailable is
  // surfaced to the panel as a "Raycasting non disponibile" warning when
  // the active adapter exposes no raycast.
  getMeasurementsSnapshot: () => MeasurementsSnapshot;
  getRaycastAvailable: () => boolean;
  // 4.I1: walk mode (FPV) state.
  getWalkSnapshot: () => WalkSnapshot;
  // WS5 PR4a (Codex iter3 add#1): input mode stabile installato al toggle.
  // Letto dal walk-hud per overlay (WASD vs touch hint) consistente con i
  // listener montati (pointerCoarse hot-switch non rimonta walk handlers).
  getWalkInputMode: () => 'keyboard' | 'touch' | null;
  // WS5 PR3.3a: viewbox state (multi-viewbox navigation) seed.
  // Live updates via 'viewbox-state' event. La handler effettivo
  // (orchestrator.selectViewbox) viene cablato in `loadedCallbacks.onSelectViewbox`
  // (main/embed/studio-bridge); il renderer non lo chiama mai direttamente.
  getViewboxSnapshot: () => ViewboxSnapshot;
  // WS5 PR3.3b: coach first-run dismiss flag iniziale (read da localStorage
  // via coach-storage.ts isCoachDismissed). Il renderer combina questo con
  // pointerCoarse (no coach su mobile MVP) + viewboxSnapshot.firstPersonAvailable
  // + per-session dismiss flag per derivare `coachVisible` in props Loaded.
  isCoachDismissed: () => boolean;
  // WS5 PR3.3c: callback per riaprire il coach (Help button toolbar).
  // Azzera il flag localStorage + il renderer azzera anche il flag in-memory
  // per-session prima di forzare il re-render.
  resetCoachDismissed: () => void;
  // WS5 PR3.3c: snapshot cinematic auto-tour seed. Live updates via evento
  // 'cinematic-state' su orchestratorEvents.
  getCinematicActive: () => boolean;
  // WS5 PR3.3c: override opzionale per disabilitare help / cinematic in
  // contesti specifici (es. Studio Web — autori non vedono il coach C1,
  // help button sarebbe no-op visivo). undefined → derived da snapshot
  // standard (pointerCoarse / viewboxes>=2).
  getHelpAvailableOverride?: () => boolean;
  getCinematicAvailableOverride?: () => boolean;
  // 4.K: WebXR (VR / AR) modal + session state.
  getXRSnapshot: () => XRSnapshot;
  // 4.M: device capability snapshot. Live updates arrive via the
  // 'device-state' event on orchestratorEvents; the getter is for the
  // initial mount only.
  getDeviceCapabilities: () => DeviceCapabilities;
  // Inc D: post-FX snapshot seed (initial mount). Live updates arrive via the
  // 'post-fx-state' event on orchestratorEvents.
  getPostFxSnapshot: () => PostFxSnapshot;
}

export interface Renderer {
  start(): void;
  dispose(): void;
}

interface MountedComponent {
  kind: AppState['kind'];
  // biome-ignore lint/suspicious/noExplicitAny: dispatcher selects the concrete props by kind
  instance: ComponentInstance<any>;
}

// 5.C: ensure the embedded scoped stylesheet is present. Idempotent.
// Selectors apply ONLY under [data-viewer-layout="embedded"] so the
// fullscreen viewer is unchanged. The strategy is a single attribute
// selector that swaps ANY descendant with `position: fixed` to
// `position: absolute`, anchored to the wrapper which already has
// `position: relative; overflow: hidden`. No per-component code change.
function ensureEmbeddedStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('viewer-ui-style-embedded') !== null) return;
  const style = document.createElement('style');
  style.id = 'viewer-ui-style-embedded';
  style.textContent = `
    [data-viewer-layout="embedded"] [style*="position:fixed"],
    [data-viewer-layout="embedded"] [style*="position: fixed"] {
      position: absolute !important;
    }
  `;
  document.head.appendChild(style);
}

function ensureGlobalStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('viewer-ui-style') !== null) return;
  const style = document.createElement('style');
  style.id = 'viewer-ui-style';
  // 4.M: dvh fallback covers html/body/#app + canvas/uiRoot so iOS
  // Safari URL bar collapse doesn't leave the page taller than the
  // visual viewport. Tap target rule scoped to coarse pointer keeps
  // desktop unaffected. Hotspot dot uses background-clip:content-box
  // so the 8px padding expansion grows the hit area without painting
  // a larger purple circle. Reduced motion zeroes transitions +
  // animations across the board so tour/hotspot fly snap immediately.
  style.textContent = `
    .viewer-ui-interactive { pointer-events: auto; }
    @keyframes viewer-spin { to { transform: rotate(360deg); } }
    .animate-spin { animation: viewer-spin 1s linear infinite; transform-origin: center; }
    @media (prefers-reduced-motion: reduce) {
      .animate-spin { animation: none; }
      *, *::before, *::after {
        transition-duration: 0.001ms !important;
        animation-duration: 0.001ms !important;
      }
    }
    @supports (height: 100dvh) {
      html, body, #app, #viewer-canvas-layer, #viewer-ui-root {
        height: 100dvh !important;
        min-height: 100dvh !important;
      }
      #viewer-canvas {
        width: 100vw !important;
        height: 100dvh !important;
      }
    }
    body[data-pointer-coarse="true"] .viewer-ui-tap-target {
      min-width: 2.75rem;
      min-height: 2.75rem;
    }
    body[data-pointer-coarse="true"] .viewer-ui-hotspot-dot-button {
      width: 44px !important;
      height: 44px !important;
      margin-left: -22px !important;
      margin-top: -22px !important;
      padding: 8px !important;
      box-sizing: border-box !important;
      background-clip: content-box !important;
    }
  `;
  document.head.appendChild(style);
}

export function createRenderer(deps: RendererDeps): Renderer {
  const layout: ViewerLayout = deps.layout ?? 'fullscreen';
  // 5.C: Mark the root so descendant components can swap position:fixed
  // → position:absolute via a single attribute selector. Read once at
  // mount, no live updates.
  deps.uiRoot.setAttribute('data-viewer-layout', layout);
  // 5.C: skip the global stylesheet for embedded runtimes — the
  // 100dvh / #viewer-canvas-layer rules would steal layout from the
  // Studio container. The wrapper sets size via position:absolute.
  if (layout === 'fullscreen') {
    ensureGlobalStyle();
  } else {
    ensureEmbeddedStyle();
  }
  let mounted: MountedComponent | null = null;
  let unsubscribe: (() => void) | null = null;
  let disposed = false;
  // Local UI-only state owned by the renderer (not the store): perf HUD
  // visibility + last FPS sample. Updated via orchestrator events.
  let showPerfHud = false;
  let currentFps = 0;
  let dismissShareToast: (() => void) | null = null;
  // 4.F: cached snapshot, refreshed on every 'tour-state' event so updates
  // propagate to the mounted Loaded component without re-querying.
  let currentTourSnapshot: TourSnapshot = deps.getTourSnapshot();
  // 4.G: same caching pattern for hotspot state + per-frame screen positions.
  let currentHotspotsSnapshot: HotspotsSnapshot = deps.getHotspotsSnapshot();
  let currentHotspotPositions: readonly HotspotScreenPosition[] = [];
  // 4.H: floor plan minimap state + per-frame projected camera position.
  let currentFloorplanSnapshot: FloorplanSnapshot = deps.getFloorplanSnapshot();
  let currentFloorplanPosition: FloorplanPosition | null = null;
  // 4.J: measurement state + per-frame projections + cumulative distances.
  let currentMeasurementsSnapshot: MeasurementsSnapshot = deps.getMeasurementsSnapshot();
  let currentMeasurementProjections: readonly MeasurementProjection[] = [];
  let currentMeasurementDistances: readonly number[] = [];
  // 4.I1: walk mode snapshot.
  let currentWalkSnapshot: WalkSnapshot = deps.getWalkSnapshot();
  // WS5 PR4a: input mode installato (touch | keyboard | null).
  let currentWalkInputMode: 'keyboard' | 'touch' | null = deps.getWalkInputMode();
  // WS5 PR3.3a: viewbox snapshot seed dall'orchestrator. Listener su
  // 'viewbox-state' aggiorna currentViewboxSnapshot + updateLoadedIfMounted.
  let currentViewboxSnapshot: ViewboxSnapshot = deps.getViewboxSnapshot();
  // WS5 PR3.3b: coach state per-session. Diventa true al dismiss (CTA o
  // close button) e non re-emerge finche' la sessione e' aperta. La
  // persistenza cross-session e' nel localStorage helper (coach-storage.ts)
  // consumato da main/embed nel callback onDismissCoach.
  let coachDismissedThisSession = false;
  // WS5 PR3.3c: snapshot cinematic auto-tour. Seed dall'orchestrator a mount,
  // aggiornato via 'cinematic-state' event (active: boolean).
  let currentCinematicActive: boolean = deps.getCinematicActive();
  // 4.K: WebXR modal + session snapshot.
  let currentXRSnapshot: XRSnapshot = deps.getXRSnapshot();
  // 4.M: device capability snapshot. Updated via 'device-state'
  // event; the initial value comes from the orchestrator's matcher
  // snapshot at start().
  let currentDeviceCapabilities: DeviceCapabilities = deps.getDeviceCapabilities();
  // Inc D: post-FX snapshot. Updated via 'post-fx-state' event.
  let currentPostFxSnapshot: PostFxSnapshot = deps.getPostFxSnapshot();
  let dismissWalkErrorToast: (() => void) | null = null;

  const currentViewport = (): { width: number; height: number } => ({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const updateLoadedIfMounted = (): void => {
    if (mounted?.kind !== 'loaded') return;
    const state = deps.store.getState();
    if (state.kind !== 'loaded') return;
    mounted.instance.update({
      filename: state.meta.filename,
      embed: deps.embed,
      showPerfHud,
      fps: currentFps,
      presets: deps.getSortedPresets(),
      tourSnapshot: currentTourSnapshot,
      hotspots: deps.getSortedHotspots(),
      hotspotsSnapshot: currentHotspotsSnapshot,
      hotspotPositions: currentHotspotPositions,
      viewport: currentViewport(),
      floorplan: deps.getFloorplan(),
      floorplanSnapshot: currentFloorplanSnapshot,
      floorplanPosition: currentFloorplanPosition,
      measurementsSnapshot: currentMeasurementsSnapshot,
      measurementProjections: currentMeasurementProjections,
      measurementDistances: currentMeasurementDistances,
      raycastAvailable: deps.getRaycastAvailable(),
      walkSnapshot: currentWalkSnapshot,
      walkInputMode: currentWalkInputMode,
      viewboxSnapshot: currentViewboxSnapshot,
      coachVisible: deriveCoachVisible(),
      xrSnapshot: currentXRSnapshot,
      pointerCoarse: currentDeviceCapabilities.pointerCoarse,
      postFxAvailable: currentPostFxSnapshot.available,
      postFxActive: currentPostFxSnapshot.userPreference,
      cinematicAvailable: deriveCinematicAvailable(),
      cinematicActive: currentCinematicActive,
      helpAvailable: deriveHelpAvailable(),
      firstPersonAvailable: currentViewboxSnapshot.firstPersonAvailable,
      mobileFpEnabled: isMobileFpEnabled(),
    });
  };

  // WS5 PR3.3b: wrapper LoadedCallbacks per coach. handleCoachDismiss
  // setta flag in-session + delega a deps.loadedCallbacks.onDismissCoach
  // (persist localStorage) + forza re-render. Senza re-render esplicito,
  // updateLoadedIfMounted non gira finche' un altro evento orchestrator
  // arriva, e il coach resterebbe visibile fino al prossimo update.
  const handleCoachDismiss = (): void => {
    coachDismissedThisSession = true;
    deps.loadedCallbacks.onDismissCoach();
    updateLoadedIfMounted();
  };
  // WS5 PR3.3c: Help button → reset coach (in-memory + localStorage) +
  // forza re-render. La gesture click utente non garantisce pointer-lock:
  // l'utente leggera' il coach + premera' CTA "Entra" per attivarlo.
  const handleHelpCoach = (): void => {
    coachDismissedThisSession = false;
    deps.resetCoachDismissed();
    deps.loadedCallbacks.onHelpCoach();
    updateLoadedIfMounted();
  };
  const wrappedLoadedCallbacks: LoadedCallbacks = {
    ...deps.loadedCallbacks,
    onDismissCoach: handleCoachDismiss,
    onHelpCoach: handleHelpCoach,
  };

  // WS5 PR3.3b: deride visibility coach da 5 condizioni AND:
  //   - scena ha viewboxes navigabili (firstPersonAvailable).
  //   - device desktop (no coach mobile MVP — PR4 introdurra' touch
  //     coach quando mobile FP e' wired).
  //   - utente non ha gia' dismisso in questa sessione (in-memory).
  //   - utente non ha gia' dismisso in sessione precedente (localStorage).
  //   - walk non gia' attivo (se l'utente sa gia' camminare, no coach).
  const deriveCoachVisible = (): boolean => {
    if (!currentViewboxSnapshot.firstPersonAvailable) return false;
    if (currentDeviceCapabilities.pointerCoarse) return false;
    if (coachDismissedThisSession) return false;
    if (currentWalkSnapshot.active) return false;
    if (deps.isCoachDismissed()) return false;
    return true;
  };

  // WS5 PR3.3c: helpers per toolbar cinematic+help availability. Default
  // logic: cinematic se viewbox>=2 (multi-stanza), help se desktop.
  // Studio Web override entrambi a false (vedi viewer-bridge.ts).
  const deriveCinematicAvailable = (): boolean => {
    if (deps.getCinematicAvailableOverride !== undefined) {
      return deps.getCinematicAvailableOverride();
    }
    return currentViewboxSnapshot.available.length >= 2;
  };
  const deriveHelpAvailable = (): boolean => {
    if (deps.getHelpAvailableOverride !== undefined) {
      return deps.getHelpAvailableOverride();
    }
    return !currentDeviceCapabilities.pointerCoarse;
  };

  const handlePerfToggle: EventListener = (event) => {
    const detail = (event as CustomEvent<{ visible: boolean }>).detail;
    showPerfHud = detail?.visible ?? !showPerfHud;
    updateLoadedIfMounted();
  };

  const handleFpsUpdate: EventListener = (event) => {
    const detail = (event as CustomEvent<{ fps: number }>).detail;
    if (typeof detail?.fps !== 'number') return;
    currentFps = detail.fps;
    if (showPerfHud) updateLoadedIfMounted();
  };

  const handleFullscreenError: EventListener = (event) => {
    const detail = (event as CustomEvent<{ message: string }>).detail;
    console.warn('[renderer] fullscreen error:', detail?.message ?? 'unknown');
  };

  const handleTourState: EventListener = (event) => {
    const detail = (event as CustomEvent<TourSnapshot>).detail;
    if (detail === undefined) return;
    currentTourSnapshot = detail;
    updateLoadedIfMounted();
  };

  const handleHotspotState: EventListener = (event) => {
    const detail = (event as CustomEvent<HotspotsSnapshot>).detail;
    if (detail === undefined) return;
    currentHotspotsSnapshot = detail;
    updateLoadedIfMounted();
  };

  const handleHotspotPositions: EventListener = (event) => {
    const detail = (event as CustomEvent<{ positions: readonly HotspotScreenPosition[] }>).detail;
    if (detail === undefined) return;
    currentHotspotPositions = detail.positions;
    updateLoadedIfMounted();
  };

  const handleFloorplanState: EventListener = (event) => {
    const detail = (event as CustomEvent<FloorplanSnapshot>).detail;
    if (detail === undefined) return;
    currentFloorplanSnapshot = detail;
    updateLoadedIfMounted();
  };

  const handleFloorplanPosition: EventListener = (event) => {
    const detail = (event as CustomEvent<FloorplanPosition>).detail;
    if (detail === undefined) return;
    currentFloorplanPosition = detail;
    updateLoadedIfMounted();
  };

  const handleMeasurementState: EventListener = (event) => {
    const detail = (event as CustomEvent<MeasurementsSnapshot>).detail;
    if (detail === undefined) return;
    currentMeasurementsSnapshot = detail;
    // 4.J Codex iter 1 MAJOR fix: when pointCount drops to 0 (RESET_ALL,
    // EXIT clearing implicit, etc.) the orchestrator stops emitting
    // 'measurement-projections', so the cached projections/distances would
    // otherwise show the last measurement forever. Truncate them in lockstep
    // so overlay and panel render an empty state immediately after reset.
    if (detail.pointCount === 0) {
      currentMeasurementProjections = [];
      currentMeasurementDistances = [];
    }
    updateLoadedIfMounted();
  };

  const handleWalkState: EventListener = (event) => {
    const detail = (event as CustomEvent<WalkSnapshot>).detail;
    if (detail === undefined) return;
    currentWalkSnapshot = detail;
    // WS5 PR4a: walk-state cambia → ri-leggi walkInputMode (settato/resettato
    // dall'orchestrator nel run effects). Senza questo, l'HUD usa lo stato
    // del seed e ignora le successive sessioni walk.
    currentWalkInputMode = deps.getWalkInputMode();
    updateLoadedIfMounted();
  };

  // WS5 PR3.3a: viewbox-state listener. Aggiorna currentViewboxSnapshot e
  // re-render Loaded (viewboxSwitcher + walk-hud pill nome reactive).
  const handleViewboxState: EventListener = (event) => {
    const detail = (event as CustomEvent<ViewboxSnapshot>).detail;
    if (detail === undefined) return;
    currentViewboxSnapshot = detail;
    updateLoadedIfMounted();
  };

  // WS5 PR3.3c: cinematic-state listener. Aggiorna currentCinematicActive
  // → toolbar reconcile aria-pressed + label switch (Tour ↔ Salta tour).
  const handleCinematicState: EventListener = (event) => {
    const detail = (event as CustomEvent<{ active: boolean }>).detail;
    if (detail === undefined) return;
    currentCinematicActive = detail.active;
    updateLoadedIfMounted();
  };

  const handleXRState: EventListener = (event) => {
    const detail = (event as CustomEvent<XRSnapshot>).detail;
    if (detail === undefined) return;
    currentXRSnapshot = detail;
    updateLoadedIfMounted();
  };

  const handleDeviceState: EventListener = (event) => {
    const detail = (event as CustomEvent<DeviceCapabilities>).detail;
    if (detail === undefined) return;
    currentDeviceCapabilities = detail;
    updateLoadedIfMounted();
  };

  // Inc D: post-FX state changed (toggle / tour start-exit / device change).
  const handlePostFxState: EventListener = (event) => {
    const detail = (event as CustomEvent<PostFxSnapshot>).detail;
    if (detail === undefined) return;
    currentPostFxSnapshot = detail;
    updateLoadedIfMounted();
  };

  // 5.C iter 6 fix: hotspot/preset cardinality crossed 0↔N (Studio CRUD
  // post-load). The Loaded component's child mounts (mountHotspotDots,
  // mountPresetPill) are no-op when wired with empty initial lists, so
  // a remount is required to surface newly-created overlays.
  const handleMetadataReplaced: EventListener = () => {
    if (mounted?.kind !== 'loaded') return;
    const state = deps.store.getState();
    if (state.kind !== 'loaded') return;
    mounted.instance.cleanup();
    mounted = null;
    renderState(state);
  };

  // 5.D: floor plan replaced (cardinality 0↔1 OR new image_url). Same
  // remount pattern as metadata-replaced — cleanup the Loaded mount so
  // mountFloorplanMinimap re-runs with the new orchestrator state.
  // iter 2 (a4): reset the cached floorplan-position so the dot/cone
  // don't show one frame of stale calibration after replace.
  const handleFloorplanReplaced: EventListener = () => {
    currentFloorplanPosition = null;
    if (mounted?.kind !== 'loaded') return;
    const state = deps.store.getState();
    if (state.kind !== 'loaded') return;
    mounted.instance.cleanup();
    mounted = null;
    renderState(state);
  };

  const handleWalkError: EventListener = (event) => {
    const detail = (event as CustomEvent<{ reason: WalkErrorReason; embed: boolean }>).detail;
    if (detail === undefined) return;
    if (dismissWalkErrorToast !== null) {
      dismissWalkErrorToast();
    }
    dismissWalkErrorToast = showWalkErrorToast(deps.uiRoot, {
      reason: detail.reason,
      embed: detail.embed,
    });
  };

  const handleMeasurementProjections: EventListener = (event) => {
    const detail = (
      event as CustomEvent<{
        projections: readonly MeasurementProjection[];
        distances: readonly number[];
      }>
    ).detail;
    if (detail === undefined) return;
    currentMeasurementProjections = detail.projections;
    currentMeasurementDistances = detail.distances;
    updateLoadedIfMounted();
  };

  const handleShareResult: EventListener = (event) => {
    const detail = (event as CustomEvent<{ success: boolean; message?: string }>).detail;
    if (dismissShareToast !== null) {
      dismissShareToast();
    }
    dismissShareToast = showShareToast(deps.uiRoot, {
      success: detail?.success ?? false,
      message: detail?.message,
    });
  };

  const renderState = (state: AppState): void => {
    if (disposed) return;

    if (mounted !== null && mounted.kind === state.kind) {
      switch (state.kind) {
        case 'loading-scene':
          mounted.instance.update({
            filename: state.meta.filename,
            clientName: state.meta.client_name,
            modelProgress: state.modelProgress,
            sizeBytes: state.meta.size_bytes,
            embed: deps.embed,
          });
          return;
        case 'error-transient':
          mounted.instance.update({
            reason: state.reason,
            attempt: state.attempt,
            canRetry: state.canRetry,
            embed: deps.embed,
          });
          return;
        case 'error-expired':
          mounted.instance.update({ expiresAt: state.expiresAt, embed: deps.embed });
          return;
        case 'loaded':
          mounted.instance.update({
            filename: state.meta.filename,
            embed: deps.embed,
            showPerfHud,
            fps: currentFps,
            presets: deps.getSortedPresets(),
            tourSnapshot: currentTourSnapshot,
            hotspots: deps.getSortedHotspots(),
            hotspotsSnapshot: currentHotspotsSnapshot,
            hotspotPositions: currentHotspotPositions,
            viewport: currentViewport(),
            floorplan: deps.getFloorplan(),
            floorplanSnapshot: currentFloorplanSnapshot,
            floorplanPosition: currentFloorplanPosition,
            measurementsSnapshot: currentMeasurementsSnapshot,
            measurementProjections: currentMeasurementProjections,
            measurementDistances: currentMeasurementDistances,
            raycastAvailable: deps.getRaycastAvailable(),
            walkSnapshot: currentWalkSnapshot,
            walkInputMode: currentWalkInputMode,
            viewboxSnapshot: currentViewboxSnapshot,
            coachVisible: deriveCoachVisible(),
            xrSnapshot: currentXRSnapshot,
            pointerCoarse: currentDeviceCapabilities.pointerCoarse,
            postFxAvailable: currentPostFxSnapshot.available,
            postFxActive: currentPostFxSnapshot.userPreference,
            cinematicAvailable: deriveCinematicAvailable(),
            cinematicActive: currentCinematicActive,
            helpAvailable: deriveHelpAvailable(),
            firstPersonAvailable: currentViewboxSnapshot.firstPersonAvailable,
            mobileFpEnabled: isMobileFpEnabled(),
          });
          return;
        default:
          return;
      }
    }

    if (mounted !== null) {
      mounted.instance.cleanup();
      mounted = null;
    }

    switch (state.kind) {
      case 'idle':
      case 'parsing-url':
      case 'fetching-meta': {
        mounted = { kind: state.kind, instance: mountIdle(deps.uiRoot) };
        return;
      }
      case 'loading-scene': {
        mounted = {
          kind: state.kind,
          instance: mountLoading(deps.uiRoot, {
            filename: state.meta.filename,
            clientName: state.meta.client_name,
            modelProgress: state.modelProgress,
            sizeBytes: state.meta.size_bytes,
            embed: deps.embed,
          }),
        };
        return;
      }
      case 'loaded': {
        // 4.F + 4.G + 4.H + 4.J: refresh cached snapshots on entry — events
        // may have arrived before this state mount.
        currentTourSnapshot = deps.getTourSnapshot();
        currentHotspotsSnapshot = deps.getHotspotsSnapshot();
        currentFloorplanSnapshot = deps.getFloorplanSnapshot();
        currentMeasurementsSnapshot = deps.getMeasurementsSnapshot();
        currentWalkSnapshot = deps.getWalkSnapshot();
        currentWalkInputMode = deps.getWalkInputMode();
        currentViewboxSnapshot = deps.getViewboxSnapshot();
        currentCinematicActive = deps.getCinematicActive();
        currentXRSnapshot = deps.getXRSnapshot();
        currentDeviceCapabilities = deps.getDeviceCapabilities();
        currentPostFxSnapshot = deps.getPostFxSnapshot();
        mounted = {
          kind: state.kind,
          instance: mountLoaded(
            deps.uiRoot,
            {
              filename: state.meta.filename,
              embed: deps.embed,
              showPerfHud,
              fps: currentFps,
              presets: deps.getSortedPresets(),
              tourSnapshot: currentTourSnapshot,
              hotspots: deps.getSortedHotspots(),
              hotspotsSnapshot: currentHotspotsSnapshot,
              hotspotPositions: currentHotspotPositions,
              viewport: currentViewport(),
              floorplan: deps.getFloorplan(),
              floorplanSnapshot: currentFloorplanSnapshot,
              floorplanPosition: currentFloorplanPosition,
              measurementsSnapshot: currentMeasurementsSnapshot,
              measurementProjections: currentMeasurementProjections,
              measurementDistances: currentMeasurementDistances,
              raycastAvailable: deps.getRaycastAvailable(),
              walkSnapshot: currentWalkSnapshot,
              walkInputMode: currentWalkInputMode,
              viewboxSnapshot: currentViewboxSnapshot,
              coachVisible: deriveCoachVisible(),
              xrSnapshot: currentXRSnapshot,
              pointerCoarse: currentDeviceCapabilities.pointerCoarse,
              postFxAvailable: currentPostFxSnapshot.available,
              postFxActive: currentPostFxSnapshot.userPreference,
              cinematicAvailable: deriveCinematicAvailable(),
              cinematicActive: currentCinematicActive,
              helpAvailable: deriveHelpAvailable(),
              firstPersonAvailable: currentViewboxSnapshot.firstPersonAvailable,
              mobileFpEnabled: isMobileFpEnabled(),
            },
            wrappedLoadedCallbacks,
          ),
        };
        return;
      }
      case 'error-not-found': {
        mounted = {
          kind: state.kind,
          instance: mountErrorNotFound(deps.uiRoot, { embed: deps.embed }),
        };
        return;
      }
      case 'error-expired': {
        mounted = {
          kind: state.kind,
          instance: mountErrorExpired(deps.uiRoot, {
            expiresAt: state.expiresAt,
            embed: deps.embed,
          }),
        };
        return;
      }
      case 'error-transient': {
        mounted = {
          kind: state.kind,
          instance: mountErrorTransient(
            deps.uiRoot,
            {
              reason: state.reason,
              attempt: state.attempt,
              canRetry: state.canRetry,
              embed: deps.embed,
            },
            { onRetry: deps.onRetry },
          ),
        };
        return;
      }
      case 'upload-fallback': {
        mounted = {
          kind: state.kind,
          instance: mountUploadFallback(deps.uiRoot, { embed: deps.embed }),
        };
        return;
      }
      case 'cancelled-by-user': {
        mounted = {
          kind: state.kind,
          instance: mountCancelledByUser(
            deps.uiRoot,
            { meta: state.meta, embed: deps.embed },
            { onRetry: deps.onRetry },
          ),
        };
        return;
      }
    }
  };

  return {
    start() {
      if (disposed) return;
      deps.orchestratorEvents.addEventListener('perf-toggle', handlePerfToggle);
      deps.orchestratorEvents.addEventListener('fps-update', handleFpsUpdate);
      deps.orchestratorEvents.addEventListener('fullscreen-error', handleFullscreenError);
      deps.orchestratorEvents.addEventListener('share-result', handleShareResult);
      deps.orchestratorEvents.addEventListener('tour-state', handleTourState);
      deps.orchestratorEvents.addEventListener('hotspot-state', handleHotspotState);
      deps.orchestratorEvents.addEventListener('hotspot-positions', handleHotspotPositions);
      deps.orchestratorEvents.addEventListener('floorplan-state', handleFloorplanState);
      deps.orchestratorEvents.addEventListener('floorplan-position', handleFloorplanPosition);
      deps.orchestratorEvents.addEventListener('measurement-state', handleMeasurementState);
      deps.orchestratorEvents.addEventListener(
        'measurement-projections',
        handleMeasurementProjections,
      );
      deps.orchestratorEvents.addEventListener('walk-state', handleWalkState);
      deps.orchestratorEvents.addEventListener('viewbox-state', handleViewboxState);
      deps.orchestratorEvents.addEventListener('cinematic-state', handleCinematicState);
      deps.orchestratorEvents.addEventListener('xr-state', handleXRState);
      deps.orchestratorEvents.addEventListener('device-state', handleDeviceState);
      deps.orchestratorEvents.addEventListener('post-fx-state', handlePostFxState);
      deps.orchestratorEvents.addEventListener('walk-error', handleWalkError);
      deps.orchestratorEvents.addEventListener('metadata-replaced', handleMetadataReplaced);
      deps.orchestratorEvents.addEventListener('floorplan-replaced', handleFloorplanReplaced);
      renderState(deps.store.getState());
      unsubscribe = deps.store.subscribe(renderState);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      deps.orchestratorEvents.removeEventListener('perf-toggle', handlePerfToggle);
      deps.orchestratorEvents.removeEventListener('fps-update', handleFpsUpdate);
      deps.orchestratorEvents.removeEventListener('fullscreen-error', handleFullscreenError);
      deps.orchestratorEvents.removeEventListener('share-result', handleShareResult);
      deps.orchestratorEvents.removeEventListener('tour-state', handleTourState);
      deps.orchestratorEvents.removeEventListener('hotspot-state', handleHotspotState);
      deps.orchestratorEvents.removeEventListener('hotspot-positions', handleHotspotPositions);
      deps.orchestratorEvents.removeEventListener('floorplan-state', handleFloorplanState);
      deps.orchestratorEvents.removeEventListener('floorplan-position', handleFloorplanPosition);
      deps.orchestratorEvents.removeEventListener('measurement-state', handleMeasurementState);
      deps.orchestratorEvents.removeEventListener(
        'measurement-projections',
        handleMeasurementProjections,
      );
      deps.orchestratorEvents.removeEventListener('walk-state', handleWalkState);
      deps.orchestratorEvents.removeEventListener('viewbox-state', handleViewboxState);
      deps.orchestratorEvents.removeEventListener('cinematic-state', handleCinematicState);
      deps.orchestratorEvents.removeEventListener('xr-state', handleXRState);
      deps.orchestratorEvents.removeEventListener('device-state', handleDeviceState);
      deps.orchestratorEvents.removeEventListener('post-fx-state', handlePostFxState);
      deps.orchestratorEvents.removeEventListener('walk-error', handleWalkError);
      deps.orchestratorEvents.removeEventListener('metadata-replaced', handleMetadataReplaced);
      deps.orchestratorEvents.removeEventListener('floorplan-replaced', handleFloorplanReplaced);
      if (unsubscribe !== null) {
        unsubscribe();
        unsubscribe = null;
      }
      if (mounted !== null) {
        mounted.instance.cleanup();
        mounted = null;
      }
      if (dismissShareToast !== null) {
        dismissShareToast();
        dismissShareToast = null;
      }
      if (dismissWalkErrorToast !== null) {
        dismissWalkErrorToast();
        dismissWalkErrorToast = null;
      }
    },
  };
}
